import { createSelector } from 'reselect'
import { checkoutAccommodationGroupingKey } from 'checkout/selectors/view/accommodation'
import { areOccupantsEqual, mergeOccupants } from 'lib/offer/occupancyUtils'
import { mergeOccupanciesByMaxPerType, normaliseOccupancyForInsurance } from 'lib/insurance/insuranceOccupants'
import getExperienceParticipantOccupants from './getExperienceParticipantOccupants'
import { getBookingProtectionEnabledCheckoutItems } from '../view/bookingProtection'
import { groupBy, partitionBy, sum } from 'lib/array/arrayUtils'
import { excludeNullOrUndefined } from 'checkout/utils'
import { isAccommodationV2Item, isBedbankItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { isStandaloneExperience } from 'selectors/checkoutSelectors'

/**
 * Leaf selectors for booking-protection occupancy.
 * Kept out of bookingProtection.ts so insurance traveller selectors can use them without
 * pulling getTotalsWithoutInsurance (and the quote-params init cycle).
 */

const getBookingProtectionOccupants = createSelector(
  (state: App.State) => getBookingProtectionEnabledCheckoutItems(state),
  (items): Array<App.Occupants> | undefined => {
    if (items.length > 0) {
      const [accommodationItems, flightItems] = partitionBy(items, (item) => (isInstantBookingLEHotelItem(item) || isBedbankItem(item) || isAccommodationV2Item(item)))
      const itemMap = groupBy(accommodationItems, checkoutAccommodationGroupingKey)

      const bookingProtectionOccupantsByAccommodationGroup = Array.from(itemMap.values()).map((itemList) => {
        const occupants = itemList
          .map(item => 'occupancy' in item ? item.occupancy : null)
          .filter(excludeNullOrUndefined)
        return mergeOccupants(occupants)
      })

      const bookingProtectionOccupantsByFlight = flightItems.map(item => 'occupants' in item ? item.occupants : null).filter(excludeNullOrUndefined)

      /**
       * Every source is normalised before the consistency gate so a teenager
       * counts as the same adult on both sides — flights included (a no-op for
       * them, they never carry teenagers) to keep one invariant: everything
       * compared or merged downstream speaks adult/child/infant.
       */
      return [
        ...bookingProtectionOccupantsByAccommodationGroup,
        ...bookingProtectionOccupantsByFlight,
      ].flat().map(normaliseOccupancyForInsurance)
    }
  },
)

export const areBookingProtectionOccupantsConsistent = createSelector(
  (state: App.State) => getBookingProtectionOccupants(state),
  (occupants): boolean => {
    return occupants ? areOccupantsEqual(...occupants) : true // if no occupants, that's consistent
  },
)

/**
 * Pre-view fallback occupancy for standalone experience carts: raw ticket counts,
 * everyone an adult. Participant expansion (participantsPerUnit, adult/child fares)
 * lives on the experience item view, which needs the offer + times fetched — until
 * then getExperienceParticipantOccupants is undefined. Falling back keeps
 * occupant-gated selectors (e.g. isBookingProtectionEnabled) truthy in that window
 * so a selected booking-protection item isn't dropped from the cart. Quotes are
 * never fetched from these counts: the quote requests are also gated on cover
 * amount / dates, which come from the same view data.
 */
export const getExperienceTicketCountFallbackOccupants = createSelector(
  (state: App.State) => getBookingProtectionEnabledCheckoutItems(state),
  (items): App.Occupants | undefined => {
    const totalTickets = sum(
      items,
      (item) => {
        if ('tickets' in item) {
          return sum(item.tickets, (ticket) => ticket.count)
        }
        return 0
      },
    )

    if (totalTickets > 0) {
      return {
        adults: totalTickets,
        children: 0,
        infants: 0,
        childrenAge: [],
      }
    }
  },
)

export function getBookingProtectionTicketCount(occupants: App.Occupants | undefined): number {
  return (occupants?.adults ?? 0) +
    (occupants?.teenagers ?? 0) +
    (occupants?.children ?? 0) +
    (occupants?.infants ?? 0)
}

export const getBookingProtectionOccupantsFromCartItems = createSelector(
  (state: App.State) => getBookingProtectionOccupants(state),
  (state: App.State) => areBookingProtectionOccupantsConsistent(state),
  (state: App.State) => isStandaloneExperience(state),
  (state: App.State) => getExperienceTicketCountFallbackOccupants(state),
  (state: App.State) => getExperienceParticipantOccupants(state),
  (occupants, isConsistent, isStandaloneExperienceCheckout, experienceFallbackOccupants, experienceParticipantOccupants) => {
    if (isStandaloneExperienceCheckout) {
      // Standalone experiences: the order submits one traveller per expanded
      // participant (same expansion as the traveller form schema), so the quote
      // occupancy must count the same participants or the provider rejects the
      // booking. Raw ticket counts are only a stopgap until the view loads.
      return experienceParticipantOccupants ?? experienceFallbackOccupants
    }
    if (isConsistent && occupants) {
      const baseOccupants = occupants[0]
      // Cruise items are not booking-protection enabled items, so cruise carts have no
      // base occupancy here: stay undefined so CFMR travellers fall back to
      // getCheckoutInsuranceTravellers, which covers the cabin and experience participants.
      if (!baseOccupants) return undefined
      // Mixed carts: the order submits the svc-traveller form union (max per age group
      // across items), which includes experience participants — the quote occupancy must
      // count the same travellers or the provider rejects the booking.
      if (experienceParticipantOccupants) {
        return mergeOccupanciesByMaxPerType([baseOccupants], [experienceParticipantOccupants])
      }
      return baseOccupants
    }
  },
)
